import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CubeTransparentIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  createDrawingIfcAlignment,
  createIfcModel,
  createIfcViewerSession,
  deleteIfcModel,
  fetchIfcModels,
  updateIfcModel,
} from "../../../api/ifcViewer";
import { uploadModuleDocuments } from "../../../api/moduleDocumentUpload";
import { fetchDrawingHouses } from "../drawings/drawingStructureApi";
import { ResourceLoading, Spinner } from "../../utils/ResourceLoading";
import { IfcUploadDialog } from "./ifc-upload-dialog";
import {
  IfcStoreyLinkDialog,
  clampNudgeMeters,
  metersFromMaybeMillimetres,
  NUDGE_XY_MAX_M,
  NUDGE_Z_MAX_M,
} from "./ifc-storey-link-dialog";
import { copyStoreyMap, readStoreyMap, readStoreyMaps, writeStoreyMap } from "./ifc-storey-map-storage";
import { copyPlacement, readPlacements, writePlacements } from "./ifc-transform-storage";
import {
  findExistingIfcModels,
  hasSavedPlacement,
  pickPlacementSource,
  transformToReuse,
} from "./ifc-replace-placement";
import {
  collectSavedPlacements,
  filterUnlockedPersistRows,
  ifcModelTransformPayload,
  persistPutsAllSucceeded,
  placementTransform,
  planIfcTransformsPersist,
  resolvePersistRows,
} from "./ifc-transform-persist";
import { createOrReplaceIfcModel } from "./ifc-upload-replace";
import { defaultViewerProbeOrigin, rewriteViewerEmbedUrl } from "./rewriteViewerEmbedUrl";
import { probeViewerOrigin } from "./probeViewerOrigin";
import {
  VIEWER_READY_TIMEOUT_MS,
  VIEWER_SESSION_TIMEOUT_MS,
  classifyReadyTimeout,
  classifySessionHttpError,
  classifyViewerErrorCode,
  issueMessageKey,
  resolveIfcViewerPane,
  resolveSessionCreateIssue,
  withTimeout,
} from "./ifc-viewer-load-watch";
import { pickDatumModel, pickDatumModelId, sortIfcModels } from "./ifc-datum";
import { IfcLinkDrawingDialog } from "./ifc-link-drawing-dialog";
import {
  linkDrawingEnterMessages,
  linkDrawingLeaveMessages,
} from "./ifc-link-drawing-view";
import {
  IFC_PHONE_MQ,
  computeIfcHostFillPx,
  cssLengthToPx,
} from "./ifc-host-layout";

// Must match the shared contract in promang-ifc-viewer (packages/shared).
const MESSAGE_SOURCE = "promang-ifc-viewer";
const IFC_MODULE = "ifc_model";

const DEFAULT_TRANSFORM = { x: 0, y: 0, z: 0, rotX: 0, rotY: 0, rotZ: 0 };
const DEFAULT_SECTION = { enabled: false, axis: "z", t: 0.55, invert: false };

/** Exact leftover from the retired `suggestUpright` auto-apply (`rotX=90` or `rotY=-90`). */
const isLeftoverHeuristicUpright = (transform) => {
  const rotX = transform?.rotX ?? 0;
  const rotY = transform?.rotY ?? 0;
  return (rotX === 90 && rotY === 0) || (rotX === 0 && rotY === -90);
};

/** Keep rematch X/Y/Z and yaw; drop leftover heuristic tilt. */
const withoutHeuristicUpright = (transform) => {
  const next = { ...DEFAULT_TRANSFORM, ...transform };
  if (!isLeftoverHeuristicUpright(next)) return next;
  return { ...next, rotX: 0, rotY: 0 };
};

/** Disciplines follow the Norwegian building trade codes used in the drawings module. */
export const IFC_DISCIPLINES = ["ARK", "RIB", "RIV", "RIE", "LAND", "OTHER"];

/** Camera distance (metres) used when focusing a point coming from a drawing. */
const FOCUS_DISTANCE = 12;


const numberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Normalise the storey list from `ifc:storeys`.
 * Viewer sends `{ id, name, elevation, empty? }` — datum catalogue only.
 */
const normaliseStoreys = (list) =>
  (Array.isArray(list) ? list : [])
    .map((entry) => {
      if (typeof entry === "string") {
        const name = entry.trim();
        return name ? { id: name, name, empty: false } : null;
      }
      const name = (entry?.name || entry?.storeyName || "").trim();
      if (!name) return null;
      const id = entry?.id != null ? String(entry.id) : name;
      return {
        id,
        name,
        elevation: Number.isFinite(entry?.elevation) ? entry.elevation : null,
        empty: Boolean(entry?.empty),
      };
    })
    .filter(Boolean);

/** Focus point from `?focusX=&focusY=&focusZ=&storey=` on the standalone page. */
export function readIfcFocusFromSearch(search) {
  const params = new URLSearchParams(search || "");
  const x = numberOrNull(params.get("focusX"));
  const y = numberOrNull(params.get("focusY"));
  if (x === null || y === null) return null;
  return {
    x,
    y,
    z: numberOrNull(params.get("focusZ")) ?? 0,
    storey_name: params.get("storey") || null,
    drawing_id: params.get("drawing") || null,
  };
}

/** Start «Koble tegning» from Tegninger (`?linkDrawing=`). */
export function readIfcLinkDrawingFromSearch(search) {
  const params = new URLSearchParams(search || "");
  const drawingId = params.get("linkDrawing") || null;
  return drawingId ? String(drawingId) : null;
}

/**
 * Project tab that embeds the standalone Promang IFC Viewer.
 *
 * Responsibilities:
 *  - manage the project's IFC model list (upload, delete, alignment transforms)
 *  - group models into a layer list by house (drawing_house_id) and discipline
 *  - request a short-lived viewer session and host it in a sandboxed iframe
 *  - bridge host <-> viewer via postMessage (visibility, transforms, selection, focus)
 */
export function IfcViewerPanel({
  project,
  canEdit = false,
  /** Upload, rematch, persist visibility — admin only, not every editor. */
  isAdmin = false,
  initialHouseId = null,
  initialFocus = null,
  initialModelIds = null,
  /** Standalone page: also accept focus/house from the current URL. */
  readUrlParams = false,
  /** Open «Koble tegning» for this drawing once the viewer is ready. */
  initialLinkDrawingId = null,
  heightClass = "h-[70vh] min-h-0",
}) {
  const { t } = useTranslation();
  const projectId = project?.id;
  const allowEdit = Boolean(canEdit);
  const allowModels = Boolean(isAdmin);
  const canPersist = allowEdit || allowModels;

  const urlState = useMemo(() => {
    if (!readUrlParams || typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    return {
      houseId: params.get("house") || null,
      focus: readIfcFocusFromSearch(window.location.search),
      linkDrawingId: readIfcLinkDrawingFromSearch(window.location.search),
    };
  }, [readUrlParams]);

  // Serialise so a freshly built prop object on every parent render cannot
  // retrigger the session effect.
  const focusKey = JSON.stringify(initialFocus || urlState.focus || null);
  const focusPoint = useMemo(
    () => (focusKey === "null" ? null : JSON.parse(focusKey)),
    [focusKey],
  );
  const modelIdsKey = (initialModelIds || []).join(",");
  const modelIds = useMemo(
    () => (modelIdsKey ? modelIdsKey.split(",") : []),
    [modelIdsKey],
  );

  const [models, setModels] = useState([]);
  const [houses, setHouses] = useState([]);
  const [houseFilter, setHouseFilter] = useState(
    initialHouseId || urlState.houseId || ""
  );
  // Keep house filter in sync with prop/URL changes on the same mounted instance.
  useEffect(() => {
    setHouseFilter(initialHouseId || urlState.houseId || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHouseId, urlState.houseId]);
  const [session, setSession] = useState(null);
  // Why the viewer pane has no session right now, so the empty state can say
  // something more useful than "no models" when models exist but the viewer
  // couldn't actually be opened (e.g. none are ready, or the service is down).
  const [sessionIssue, setSessionIssue] = useState(null);
  // unknown until we probe :5081 (or skip in prod with no origin).
  // Must not flash the upload card while the viewer may be down.
  const [viewerHealth, setViewerHealth] = useState("unknown");
  const [loading, setLoading] = useState(true);
  const [creatingSession, setCreatingSession] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isolatedModelId, setIsolatedModelId] = useState(null);
  const [section, setSection] = useState(DEFAULT_SECTION);
  const [tool, setTool] = useState("select");
  const [measureLabel, setMeasureLabel] = useState(null);
  const [viewerReady, setViewerReady] = useState(false);
  const viewerReadyRef = useRef(false);
  // Storeys and per-model bounds are reported by the viewer once it has parsed
  // the geometry; both are empty until then, so the tools depending on them
  // stay disabled instead of guessing.
  const [storeys, setStoreys] = useState([]);
  const [storeyFilter, setStoreyFilter] = useState("");
  const [modelBoundsById, setModelBoundsById] = useState({});

  const hostRef = useRef(null);
  const iframeRef = useRef(null);
  const focusSentRef = useRef(false);
  const sessionReqIdRef = useRef(0);
  const readyWatchRef = useRef({
    timer: null,
    iframeError: false,
    sawFatalModelError: false,
  });
  const modelsRef = useRef(models);
  const pendingAlignToastRef = useRef(false);
  const pendingRemergeToastRef = useRef(false);
  const pendingPlaceToastRef = useRef(false);
  /** Model ids whose XYZ was copied on replace — rematch must not overwrite. */
  const keptPlacementIdsRef = useRef(new Set());
  const storeyProposalsRef = useRef({});
  const pendingStoreyLinkIdsRef = useRef([]);
  const pendingOpenStoreyModelIdRef = useRef(null);
  const compareBaselineRef = useRef(null);
  const compareVisibilityRef = useRef(null);
  const compareSectionRef = useRef(null);
  const lastNudgeRef = useRef({ x: 0, y: 0, z: 0 });
  const ignoreDialogTransformsRef = useRef(false);
  const appliedStoreyMapsSessionRef = useRef(null);
  const [storeyDialog, setStoreyDialog] = useState(null);
  const [storeyDialogSaving, setStoreyDialogSaving] = useState(false);
  const [linkDrawingOpen, setLinkDrawingOpen] = useState(false);
  const [linkDrawingId, setLinkDrawingId] = useState(
    initialLinkDrawingId || urlState.linkDrawingId || "",
  );
  const [linkDrawingSaving, setLinkDrawingSaving] = useState(false);
  const [overlayTransform, setOverlayTransform] = useState(null);
  const linkDrawingOpenedRef = useRef(false);
  const linkDrawingViewRef = useRef(null);
  const linkPairWaitRef = useRef(null);
  const handleHostActionRef = useRef(null);
  useEffect(() => {
    modelsRef.current = models;
  }, [models]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof window === "undefined") return undefined;

    const apply = () => {
      const phone = window.matchMedia(IFC_PHONE_MQ).matches;
      if (!phone) {
        el.style.removeProperty("height");
        el.style.removeProperty("max-height");
        el.style.removeProperty("min-height");
        return;
      }
      const top = el.getBoundingClientRect().top;
      const remPx = Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      const navPx = cssLengthToPx(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--mobile-nav-height",
        ),
        Number.isFinite(remPx) ? remPx : 16,
      );
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const height = computeIfcHostFillPx({
        top,
        viewportHeight: vh,
        bottomReserve: navPx,
      });
      el.style.minHeight = "0px";
      el.style.height = `${height}px`;
      el.style.maxHeight = `${height}px`;
    };

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    window.visualViewport?.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      window.visualViewport?.removeEventListener("resize", apply);
      el.style.removeProperty("height");
      el.style.removeProperty("max-height");
      el.style.removeProperty("min-height");
    };
  }, []);

  // Dev: swap hostname (LAN / Tailscale). Prod: keep API embed_url as-is.
  // Never copy window.location.port — that is Promang :3000.
  const viewerEmbedUrl = useMemo(() => {
    if (!session?.embed_url || typeof window === "undefined") {
      return session?.embed_url || null;
    }
    return rewriteViewerEmbedUrl(session.embed_url, window.location.hostname, {
      isProduction: process.env.NODE_ENV === "production",
    });
  }, [session]);

  const viewerOrigin = useMemo(() => {
    if (!viewerEmbedUrl) return null;
    try {
      return new URL(viewerEmbedUrl).origin;
    } catch (_) {
      return null;
    }
  }, [viewerEmbedUrl]);

  const postToViewer = useCallback(
    (message) => {
      const frame = iframeRef.current;
      if (frame?.contentWindow && viewerOrigin) {
        frame.contentWindow.postMessage(
          { ...message, source: MESSAGE_SOURCE },
          viewerOrigin,
        );
      }
    },
    [viewerOrigin],
  );

  const clearReadyWatch = useCallback(() => {
    if (readyWatchRef.current.timer) {
      window.clearTimeout(readyWatchRef.current.timer);
      readyWatchRef.current.timer = null;
    }
  }, []);

  const failEmbed = useCallback(
    (issue, requestId = sessionReqIdRef.current) => {
      if (sessionReqIdRef.current !== requestId) return;
      if (viewerReadyRef.current) return;
      clearReadyWatch();
      setSessionIssue(issue);
      if (issue === "viewer_down" || issue === "viewer_unavailable") {
        setViewerHealth("down");
      }
    },
    [clearReadyWatch],
  );

  useEffect(() => {
    if (session?.embed_url && !viewerEmbedUrl) {
      failEmbed("viewer_down");
    }
  }, [session?.embed_url, viewerEmbedUrl, failEmbed]);

  const bumpReadyWatch = useCallback(() => {
    if (viewerReadyRef.current || !viewerOrigin) return;
    const requestId = sessionReqIdRef.current;
    if (readyWatchRef.current.timer) {
      window.clearTimeout(readyWatchRef.current.timer);
    }
    readyWatchRef.current.timer = window.setTimeout(() => {
      void probeViewerOrigin(viewerOrigin).then((probe) => {
        failEmbed(
          classifyReadyTimeout({
            probedReason: probe.reason,
            iframeError: readyWatchRef.current.iframeError,
            sawFatalModelError: readyWatchRef.current.sawFatalModelError,
          }),
          requestId,
        );
      });
    }, VIEWER_READY_TIMEOUT_MS);
  }, [viewerOrigin, failEmbed]);

  const modelsReqIdRef = useRef(0);

  const loadModels = useCallback(async () => {
    if (!projectId) return [];
    const requestId = ++modelsReqIdRef.current;
    const requestedProjectId = projectId;
    const data = await fetchIfcModels(requestedProjectId);
    // Ignore responses from a previous project after a quick switch.
    if (modelsReqIdRef.current !== requestId) return [];
    const rows = data?.data || [];
    const stored = readPlacements(requestedProjectId);
    const list = rows.map((m) => {
      const confirmed =
        m.up_axis_confirmed === true ||
        stored[String(m.id)]?.uprightConfirmed === true;
      return {
        ...m,
        // `visible_by_default` is optional on the API payload; default to visible.
        visible: m.visible_by_default !== false,
        up_axis_confirmed: confirmed,
        transform: confirmed
          ? { ...DEFAULT_TRANSFORM, ...(m.transform || {}) }
          : withoutHeuristicUpright(m.transform || {}),
      };
    });
    setModels(list);
    return list;
  }, [projectId]);

  const openSession = useCallback(async () => {
    if (!projectId) return;
    const requestId = ++sessionReqIdRef.current;
    // Clear any existing iframe immediately to avoid showing stale content
    // while a new session is being created.
    setSession(null);
    setSessionIssue(null);
    setViewerHealth("unknown");
    setViewerReady(false);
    viewerReadyRef.current = false;
    clearReadyWatch();
    readyWatchRef.current = {
      timer: null,
      iframeError: false,
      sawFatalModelError: false,
    };
    setStoreys([]);
    setStoreyFilter("");
    setModelBoundsById({});
    setIsolatedModelId(null);
    setSection(DEFAULT_SECTION);
    focusSentRef.current = false;
    setCreatingSession(true);
    try {
      // Admin sees hidden models in the drawer (and can show them again).
      // Everyone else must not load deactivated models.
      const body = {};
      if (allowModels) body.include_hidden = true;
      if (houseFilter) body.drawing_house_id = houseFilter;
      if (modelIds.length) body.model_ids = modelIds;
      if (focusPoint) {
        // Only send supported focus keys to the API
        const { x, y, z, storey_name } = focusPoint;
        body.focus = {
          x,
          y,
          z: z ?? 0,
          storey_name: storey_name || null,
        };
      }
      const result = await withTimeout(
        createIfcViewerSession(projectId, body),
        VIEWER_SESSION_TIMEOUT_MS,
      );
      if (sessionReqIdRef.current === requestId) {
        setSession(result);
      }
    } catch (err) {
      // API `no_models` is empty-state only when the host also has no rows.
      // Host already listed models → session/filter miss, never the upload card.
      const issue = resolveSessionCreateIssue(classifySessionHttpError(err), {
        hostModelCount: modelsRef.current.length,
      });
      if (!issue) {
        if (sessionReqIdRef.current === requestId) {
          setSession(null);
        }
      } else {
        toast.error(t(issueMessageKey(issue)));
        if (sessionReqIdRef.current === requestId) {
          setSession(null);
          setSessionIssue(issue);
        }
      }
    } finally {
      // Only clear the loading flag if this is still the latest request.
      if (sessionReqIdRef.current === requestId) {
        setCreatingSession(false);
      }
    }
  }, [projectId, houseFilter, modelIds, focusPoint, allowModels, t, clearReadyWatch]);

  // When the project changes (including first mount), clear local state that
  // depends on it and load that project's models, in a single effect.
  //
  // These two steps used to be separate effects (reset-on-[projectId], then
  // load-on-[loadModels, t]). Both fire on every mount/project switch, and the
  // reset effect always ran *after* the load effect had already kicked off
  // its fetch — bumping `modelsReqIdRef` before the response came back. Since
  // network latency always exceeds that synchronous gap, `loadModels`'s
  // staleness guard (`modelsReqIdRef.current !== requestId`) was true on
  // every single load, so the fetched models were silently discarded and the
  // panel stayed empty until some other action (e.g. an upload) called
  // `loadModels` again on its own. Doing the reset and the load in one effect
  // keeps them in the same synchronous flush, so nothing can interleave.
  useEffect(() => {
    let cancelled = false;
    modelsReqIdRef.current += 1;
    sessionReqIdRef.current += 1;
    setModels([]);
    setSession(null);
    setSessionIssue(null);
    setViewerHealth("unknown");
    setViewerReady(false);
    viewerReadyRef.current = false;
    clearReadyWatch();
    setStoreys([]);
    setStoreyFilter("");
    setModelBoundsById({});
    setCreatingSession(false);

    (async () => {
      setLoading(true);
      try {
        await loadModels();
        if (cancelled) return;
      } catch (_) {
        if (!cancelled) {
          toast.error(t("ifc_viewer.error_load"));
          setSessionIssue("models_failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, loadModels, t, clearReadyWatch]);

  // Rebuild the viewer session when its inputs change, without reloading models.
  useEffect(() => {
    if (models.length === 0) return;
    openSession();
  }, [openSession, models.length]);

  // House list powers both the layer grouping and the upload house selector.
  useEffect(() => {
    let cancelled = false;
    if (!projectId) return undefined;
    (async () => {
      try {
        const rows = await fetchDrawingHouses(projectId);
        if (!cancelled) setHouses(rows || []);
      } catch (_) {
        /* drawings module may be off ? grouping falls back to ?no house? */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const persistViewerTransforms = useCallback(
    async (rows, { keepUprightTilt = false } = {}) => {
      const incoming = Array.isArray(rows) ? rows : [];
      if (!projectId || incoming.length === 0) {
        return { ok: false, reason: "skipped", putCount: 0, failedIds: [] };
      }
      const { resolved, failedIds } = resolvePersistRows(
        incoming,
        modelsRef.current,
        { keepUprightTilt },
      );
      writePlacements(projectId, resolved);
      if (failedIds.length) {
        console.warn("[ifc-host] persist skipped unknown model ids", {
          failedIds,
          hostIds: (modelsRef.current || []).map((model) => String(model.id)),
        });
      }
      if (!canPersist || resolved.length === 0) {
        return {
          ok: false,
          reason: resolved.length === 0 ? "unresolved_id" : "skipped",
          putCount: 0,
          failedIds,
        };
      }
      const results = await Promise.allSettled(
        resolved.map((row) =>
          updateIfcModel(
            projectId,
            row.modelId,
            ifcModelTransformPayload(row.transform, {
              keepUprightTilt: keepUprightTilt || row.uprightConfirmed === true,
            }),
          ),
        ),
      );
      const putCount = results.filter((item) => item.status === "fulfilled").length;
      const putFailedIds = results
        .map((item, index) =>
          item.status === "rejected" ? resolved[index].modelId : null,
        )
        .filter(Boolean);
      const allFailed = [...failedIds, ...putFailedIds];
      const firstError = results.find((item) => item.status === "rejected");
      console.info("[ifc-host] updateIfcModel persist", {
        putCount,
        expected: incoming.length,
        resolvedIds: resolved.map((row) => row.modelId),
        failedIds: allFailed,
      });
      return {
        ok:
          allFailed.length === 0 &&
          putCount === resolved.length &&
          resolved.length === incoming.length,
        putCount,
        failedIds: allFailed,
        reason: allFailed.length ? "error" : null,
        error: firstError?.reason,
      };
    },
    [canPersist, projectId],
  );

  // Listen for viewer -> host messages.
  useEffect(() => {
    const handler = (event) => {
      // Require a known viewer origin; ignore messages when no active session.
      if (!viewerOrigin || event.origin !== viewerOrigin) return;
      // Two panel instances can be mounted at once (e.g. the project tab and
      // the standalone viewer page) with iframes on the same origin — origin
      // alone can't tell their postMessages apart, so also require the
      // message to have come from *this* panel's own iframe.
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (!data || data.source !== MESSAGE_SOURCE) return;
      switch (data.type) {
        case "ifc:ready":
          setViewerReady(true);
          viewerReadyRef.current = true;
          clearReadyWatch();
          setSessionIssue(null);
          setViewerHealth("ok");
          break;
        case "ifc:progress":
        case "ifc:model-loaded":
          bumpReadyWatch();
          break;
        case "ifc:selection":
          if (linkPairWaitRef.current && data?.point) {
            linkPairWaitRef.current(data.point);
            postToViewer({
              type: "ifc:focus-point",
              x: data.point.x,
              y: data.point.y,
              z: data.point.z,
              distance: FOCUS_DISTANCE,
            });
          }
          break;
        case "drawing:overlay-changed":
          if (data.transform) setOverlayTransform(data.transform);
          break;
        case "ifc:section":
          setSection({
            enabled: Boolean(data.enabled),
            axis: data.axis || "z",
            t: Number.isFinite(data.t) ? data.t : 0.55,
            invert: Boolean(data.invert),
          });
          break;
        case "ifc:tool-state":
          if (data.tool) setTool(data.tool);
          if ("isolatedModelId" in data) {
            setIsolatedModelId(data.isolatedModelId ?? null);
          }
          break;
        case "ifc:measure":
          setMeasureLabel(data.kind === "cleared" ? null : data.label || null);
          break;
        case "ifc:storeys":
          setStoreys(normaliseStoreys(data.storeys || data.names));
          break;
        case "ifc:storey-map-proposal": {
          if (!data.modelId) break;
          storeyProposalsRef.current[String(data.modelId)] = data;
          const pendingIds = pendingStoreyLinkIdsRef.current;
          const wanted =
            pendingOpenStoreyModelIdRef.current ||
            pendingIds.find((id) => String(id) === String(data.modelId));
          if (wanted && String(wanted) === String(data.modelId)) {
            pendingOpenStoreyModelIdRef.current = null;
            pendingStoreyLinkIdsRef.current = pendingIds.filter(
              (id) => String(id) !== String(data.modelId),
            );
            setStoreyDialog(data);
          }
          break;
        }
        case "ifc:model-bounds":
          if (data.modelId) {
            const nested = data.bounds || {};
            setModelBoundsById((prev) => ({
              ...prev,
              [String(data.modelId)]: {
                min: nested.min || data.min || null,
                max: nested.max || data.max || null,
                center: nested.center || data.center || null,
                size: nested.size || data.size || null,
                suggestedUpright: data.suggestedUpright || null,
              },
            }));
          }
          break;
        case "ifc:request-placements": {
          const placements = collectSavedPlacements(
            modelsRef.current,
            readPlacements(projectId),
          );
          console.info("[ifc-host] ifc:saved-placements", {
            count: placements.length,
            modelIds: placements.map((row) => row.modelId),
          });
          postToViewer({ type: "ifc:saved-placements", placements });
          break;
        }
        case "ifc:transforms": {
          // Dialog Lagre writes baseline+nudge itself. Ignore rematch
          // ifc:transforms that would overwrite the slider offset.
          const confirmedIds = new Set(
            (modelsRef.current || [])
              .filter((model) => model.up_axis_confirmed === true)
              .map((model) => String(model.id)),
          );
          const planned = planIfcTransformsPersist(data, {
            ignoreDialog: ignoreDialogTransformsRef.current,
            pendingRemerge: pendingRemergeToastRef.current,
            keptIds: keptPlacementIdsRef.current,
            confirmedIds,
          });
          if (!planned.persist) break;
          const lockedIds = new Set(
            Object.entries(readPlacements(projectId))
              .filter(([, row]) => row?.federationCleared)
              .map(([id]) => String(id)),
          );
          const usable = filterUnlockedPersistRows(planned.rows, lockedIds, {
            forceRematch: planned.forceRematch || viewerReadyRef.current,
            reason: data.reason || null,
          });
          const forceRematch = planned.forceRematch;
          const keepUprightTilt = planned.keepUprightTilt === true;
          console.info("[ifc-host] ifc:transforms persist", {
            reason: data.reason || null,
            forceRematch,
            postedIds: (data.transforms || []).map((row) => row.modelId),
            usable,
            hostIds: (modelsRef.current || []).map((model) => String(model.id)),
          });
          const keptIds = keptPlacementIdsRef.current;
          setModels((prev) =>
            prev.map((m) => {
              if (!forceRematch && keptIds.has(String(m.id))) return m;
              const hit = usable.find(
                (row) => String(row.modelId) === String(m.id),
              );
              if (!hit?.transform) return m;
              const keepTilt =
                keepUprightTilt || confirmedIds.has(String(m.id));
              return {
                ...m,
                transform: placementTransform(hit.transform, {
                  keepUprightTilt: keepTilt,
                }),
                up_axis_confirmed: keepTilt ? true : m.up_axis_confirmed,
              };
            }),
          );
          void (async () => {
            const result = await persistViewerTransforms(usable, {
              keepUprightTilt,
            });
            if (keepUprightTilt) {
              if (persistPutsAllSucceeded(result, usable.length)) {
                toast.success(t("ifc_viewer.upright_saved"));
              } else {
                toast.error(t("ifc_viewer.upright_save_failed"));
              }
              return;
            }
            if (forceRematch) {
              pendingRemergeToastRef.current = false;
              if (persistPutsAllSucceeded(result, usable.length)) {
                toast.success(t("ifc_viewer.remerge_saved"));
              } else {
                toast.error(t("ifc_viewer.remerge_save_failed"));
              }
              return;
            }
            if (!result.ok && result.reason === "error") {
              pendingPlaceToastRef.current = false;
              pendingAlignToastRef.current = false;
              toast.error(t("ifc_viewer.remerge_save_failed"));
              return;
            }
            if (pendingPlaceToastRef.current) {
              pendingPlaceToastRef.current = false;
              toast.success(t("ifc_viewer.place_done"));
            } else if (pendingAlignToastRef.current) {
              pendingAlignToastRef.current = false;
              toast.success(
                t("ifc_viewer.auto_align_on_upload", {
                  count: usable.length || data.transforms.length,
                }),
              );
            }
          })();
          break;
        }
        case "ifc:error": {
          const fatal = classifyViewerErrorCode(data.code);
          if (fatal && !viewerReadyRef.current) {
            readyWatchRef.current.sawFatalModelError = fatal === "model_failed";
            failEmbed(fatal);
          }
          toast.error(data.message || t("ifc_viewer.error_viewer_generic"));
          break;
        }
        case "ifc:host-action":
          handleHostActionRef.current?.(data);
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [
    viewerOrigin,
    t,
    persistViewerTransforms,
    postToViewer,
    projectId,
    clearReadyWatch,
    failEmbed,
    bumpReadyWatch,
  ]);

  // Iframe / first model must become ready. Dead :5081 or a white crash
  // must not leave the host on a spinner forever.
  useEffect(() => {
    if (!session?.embed_url || !viewerOrigin) return undefined;
    const requestId = sessionReqIdRef.current;
    clearReadyWatch();
    readyWatchRef.current = {
      timer: null,
      iframeError: false,
      sawFatalModelError: false,
    };

    void probeViewerOrigin(viewerOrigin).then((probe) => {
      if (sessionReqIdRef.current !== requestId) return;
      if (viewerReadyRef.current) return;
      if (probe.reason === "unreachable" || probe.reason === "missing") {
        failEmbed("viewer_down", requestId);
      }
    });

    bumpReadyWatch();

    return () => {
      clearReadyWatch();
    };
  }, [session?.embed_url, session?.session_id, viewerOrigin, clearReadyWatch, failEmbed, bumpReadyWatch]);

  // No session (0 models, or session never started): still probe :5081 so a
  // dead viewer cannot fall through to the upload empty card.
  useEffect(() => {
    if (loading || creatingSession) return undefined;
    if (viewerReadyRef.current) return undefined;
    if (sessionIssue) return undefined;
    if (viewerEmbedUrl) return undefined;
    if (viewerHealth !== "unknown") return undefined;

    const origin =
      viewerOrigin ||
      (typeof window !== "undefined"
        ? defaultViewerProbeOrigin(window.location.hostname, {
            isProduction: process.env.NODE_ENV === "production",
          })
        : null);
    if (!origin) {
      setViewerHealth("ok");
      return undefined;
    }

    const requestId = sessionReqIdRef.current;
    let cancelled = false;
    void probeViewerOrigin(origin).then((probe) => {
      if (cancelled || sessionReqIdRef.current !== requestId) return;
      if (viewerReadyRef.current) return;
      if (probe.reason === "unreachable" || probe.reason === "missing") {
        setViewerHealth("down");
        failEmbed("viewer_down", requestId);
        return;
      }
      setViewerHealth("ok");
    });
    return () => {
      cancelled = true;
    };
  }, [
    loading,
    creatingSession,
    sessionIssue,
    viewerEmbedUrl,
    viewerOrigin,
    viewerHealth,
    failEmbed,
  ]);

  // Hidden models stay in the admin list but start off in the scene.
  useEffect(() => {
    if (!viewerReady) return;
    modelsRef.current.forEach((model) => {
      if (model.visible === false || model.visible_by_default === false) {
        postToViewer({
          type: "ifc:set-visibility",
          modelId: model.id,
          visible: false,
        });
      }
    });
  }, [viewerReady, postToViewer]);

  const postHostCapabilities = useCallback(() => {
    postToViewer({
      type: "ifc:host-capabilities",
      admin: allowModels,
      canLinkDrawing: Boolean(canPersist && projectId),
      models: (modelsRef.current || []).map((model) => ({
        id: String(model.id),
        visibleByDefault: model.visible_by_default !== false,
      })),
    });
  }, [allowModels, canPersist, postToViewer, projectId]);

  useEffect(() => {
    if (!viewerReady) return;
    postHostCapabilities();
  }, [viewerReady, models, postHostCapabilities]);

  // Saved storey maps cannot go on IfcModel.transform (API strips extra keys).
  // Re-apply browser maps after the viewer is ready so the next session remembers.
  useEffect(() => {
    if (!viewerReady || !projectId || !session?.session_id) return;
    if (appliedStoreyMapsSessionRef.current === session.session_id) return;
    appliedStoreyMapsSessionRef.current = session.session_id;
    const datumId = pickDatumModelId(modelsRef.current);
    const maps = readStoreyMaps(projectId);
    Object.entries(maps).forEach(([modelId, payload]) => {
      if (!payload?.links?.length) return;
      if (datumId && String(modelId) === String(datumId)) return;
      postToViewer({ type: "ifc:set-storey-map", modelId, links: payload.links });
    });
  }, [viewerReady, projectId, session?.session_id, postToViewer]);

  useEffect(() => {
    if (!viewerReady || !canPersist) return;
    const wanted = initialLinkDrawingId || urlState.linkDrawingId;
    if (!wanted || linkDrawingOpenedRef.current) return;
    linkDrawingOpenedRef.current = true;
    setLinkDrawingId(String(wanted));
    setLinkDrawingOpen(true);
  }, [viewerReady, canPersist, initialLinkDrawingId, urlState.linkDrawingId]);

  // Compare mode: keep datum + this model, top view, section at the selected storey.
  useEffect(() => {
    if (!storeyDialog?.modelId) return undefined;
    const datumId = storeyDialog.datumModelId;
    const movingId = storeyDialog.modelId;
    const current = modelsRef.current.find((m) => String(m.id) === String(movingId));
    lastNudgeRef.current = { x: 0, y: 0, z: 0 };
    compareBaselineRef.current = {
      ...DEFAULT_TRANSFORM,
      ...(current?.transform || {}),
    };
    compareVisibilityRef.current = modelsRef.current.map((m) => ({
      id: m.id,
      visible: m.visible,
    }));
    compareSectionRef.current = { ...section };
    modelsRef.current.forEach((m) => {
      const show =
        String(m.id) === String(movingId) || String(m.id) === String(datumId);
      postToViewer({ type: "ifc:set-visibility", modelId: m.id, visible: show });
    });
    postToViewer({ type: "ifc:set-view", preset: "top" });
    postToViewer({ type: "ifc:set-xray", enabled: true });
    return undefined;
    // Enter once per dialog open; section/storey updates come from the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeyDialog?.modelId, postToViewer]);

  // Jump to the point that was clicked on the drawing once the scene is up.
  useEffect(() => {
    if (!viewerReady || !focusPoint || focusSentRef.current) return;
    focusSentRef.current = true;
    postToViewer({
      type: "ifc:focus-point",
      x: focusPoint.x,
      y: focusPoint.y,
      z: focusPoint.z ?? 0,
      distance: FOCUS_DISTANCE,
      storeyName: focusPoint.storey_name || null,
      source: "drawing",
    });
  }, [viewerReady, focusPoint, postToViewer]);

  const handleToggleHidden = useCallback(
    async (modelId, visibleForEveryone) => {
      if (!allowModels || !projectId) return;
      const previous = models.find((m) => String(m.id) === String(modelId));
      setModels((prev) =>
        prev.map((m) =>
          String(m.id) === String(modelId)
            ? { ...m, visible: visibleForEveryone, visible_by_default: visibleForEveryone }
            : m,
        ),
      );
      postToViewer({
        type: "ifc:set-visibility",
        modelId,
        visible: visibleForEveryone,
      });
      try {
        await updateIfcModel(projectId, modelId, {
          visible_by_default: visibleForEveryone,
        });
        toast.success(
          visibleForEveryone
            ? t("ifc_viewer.model_shown")
            : t("ifc_viewer.model_hidden"),
        );
      } catch (_) {
        if (previous) {
          setModels((prev) =>
            prev.map((m) =>
              String(m.id) === String(modelId)
                ? {
                    ...m,
                    visible: previous.visible,
                    visible_by_default: previous.visible_by_default,
                  }
                : m,
            ),
          );
          postToViewer({
            type: "ifc:set-visibility",
            modelId,
            visible: previous.visible !== false,
          });
        }
        toast.error(t("ifc_viewer.model_hide_error"));
      }
    },
    [allowModels, models, postToViewer, projectId, t],
  );

  const handlePlaceModel = useCallback(() => {
    if (!allowModels) return;
    if (tool === "place") {
      setTool("select");
      postToViewer({ type: "ifc:set-tool", tool: "select" });
      return;
    }
    if (models.length < 2) {
      toast.error(t("ifc_viewer.place_unavailable"));
      return;
    }
    const ordered = [...models].sort((a, b) => {
      const order = (a.sort_order || 0) - (b.sort_order || 0);
      if (order !== 0) return order;
      return String(a.created_at || "").localeCompare(String(b.created_at || ""));
    });
    const datumId = ordered[0] ? String(ordered[0].id) : null;
    const movingId =
      isolatedModelId && String(isolatedModelId) !== datumId
        ? String(isolatedModelId)
        : ordered.length === 2
          ? String(ordered[1].id)
          : undefined;
    setTool("place");
    pendingPlaceToastRef.current = true;
    postToViewer({ type: "ifc:place-start", modelId: movingId });
  }, [allowModels, isolatedModelId, models, postToViewer, t, tool]);

  const handleRemerge = useCallback(() => {
    if (!allowModels) return;
    if (models.length < 2) {
      toast.error(t("ifc_viewer.auto_align_unavailable"));
      return;
    }
    const ordered = sortIfcModels(models);
    const reference =
      pickDatumModel(ordered.filter((m) => m.visible !== false)) ||
      pickDatumModel(ordered) ||
      ordered[0];
    if (!reference) {
      toast.error(t("ifc_viewer.auto_align_unavailable"));
      return;
    }
    pendingRemergeToastRef.current = true;
    postToViewer({
      type: "ifc:remerge",
      referenceModelId: String(reference.id),
    });
  }, [allowModels, models, postToViewer, t]);

  const handleReloadModels = useCallback(async () => {
    if (!allowModels) return;
    await openSession();
    toast.success(t("ifc_viewer.reload_models_done"));
  }, [allowModels, openSession, t]);

  const handleRetryViewer = useCallback(() => {
    if (modelsRef.current.length > 0) {
      void openSession();
      return;
    }
    setSessionIssue(null);
    setViewerHealth("unknown");
    const origin =
      viewerOrigin ||
      (typeof window !== "undefined"
        ? defaultViewerProbeOrigin(window.location.hostname, {
            isProduction: process.env.NODE_ENV === "production",
          })
        : null);
    if (!origin) {
      setViewerHealth("ok");
      return;
    }
    setCreatingSession(true);
    void probeViewerOrigin(origin)
      .then((probe) => {
        if (probe.reason === "unreachable" || probe.reason === "missing") {
          setViewerHealth("down");
          setSessionIssue("viewer_down");
          return;
        }
        setViewerHealth("ok");
      })
      .finally(() => {
        setCreatingSession(false);
      });
  }, [openSession, viewerOrigin]);

  const exitStoreyCompare = useCallback(
    ({ restoreTransform = false } = {}) => {
      const movingId = storeyDialog?.modelId;
      if (restoreTransform && movingId && compareBaselineRef.current) {
        postToViewer({
          type: "ifc:set-transform",
          modelId: movingId,
          transform: compareBaselineRef.current,
        });
      }
      (compareVisibilityRef.current || []).forEach((row) => {
        postToViewer({
          type: "ifc:set-visibility",
          modelId: row.id,
          visible: row.visible,
        });
      });
      if (compareSectionRef.current) {
        setSection(compareSectionRef.current);
        postToViewer({ type: "ifc:set-section", ...compareSectionRef.current });
      }
      postToViewer({ type: "ifc:set-xray", enabled: false });
      postToViewer({ type: "ifc:set-storey", storeyId: null });
      setStoreyFilter("");
    },
    [postToViewer, storeyDialog],
  );

  const openNextStoreyDialog = useCallback(() => {
    const nextId = pendingStoreyLinkIdsRef.current[0];
    if (!nextId) return;
    const cached = storeyProposalsRef.current[String(nextId)];
    if (cached) {
      pendingStoreyLinkIdsRef.current = pendingStoreyLinkIdsRef.current.filter(
        (id) => String(id) !== String(nextId),
      );
      setStoreyDialog(cached);
      return;
    }
    pendingOpenStoreyModelIdRef.current = nextId;
    postToViewer({ type: "ifc:request-storey-map", modelId: nextId });
  }, [postToViewer]);

  const persistDialogTransform = useCallback(
    async (modelId, nudge) => {
      if (!canPersist || !projectId || !modelId) return;
      const baseline = compareBaselineRef.current || DEFAULT_TRANSFORM;
      const dx = clampNudgeMeters(nudge?.x || 0, NUDGE_XY_MAX_M);
      const dy = clampNudgeMeters(nudge?.y || 0, NUDGE_XY_MAX_M);
      const dz = clampNudgeMeters(nudge?.z || 0, NUDGE_Z_MAX_M);
      const confirmed =
        modelsRef.current.find((m) => String(m.id) === String(modelId))
          ?.up_axis_confirmed === true;
      const transform = placementTransform(
        {
          ...baseline,
          x: (baseline.x || 0) + dx,
          y: (baseline.y || 0) + dy,
          z: (baseline.z || 0) + dz,
        },
        { keepUprightTilt: confirmed },
      );
      lastNudgeRef.current = { x: dx, y: dy, z: dz };
      ignoreDialogTransformsRef.current = true;
      try {
        await persistViewerTransforms([{ modelId, transform }], {
          keepUprightTilt: confirmed,
        });
        setModels((prev) =>
          prev.map((m) =>
            String(m.id) === String(modelId) ? { ...m, transform } : m,
          ),
        );
        postToViewer({
          type: "ifc:set-transform",
          modelId,
          transform,
        });
      } catch (_) {
        /* viewer still has the live transform */
      } finally {
        window.setTimeout(() => {
          ignoreDialogTransformsRef.current = false;
        }, 50);
      }
    },
    [canPersist, persistViewerTransforms, postToViewer, projectId],
  );

  const handleStoreyDialogSave = useCallback(
    async ({ links, nudge }) => {
      if (!storeyDialog?.modelId) return;
      setStoreyDialogSaving(true);
      ignoreDialogTransformsRef.current = true;
      try {
        writeStoreyMap(projectId, storeyDialog.modelId, {
          links,
          datumModelId: storeyDialog.datumModelId,
          confirmed: true,
        });
        postToViewer({
          type: "ifc:set-storey-map",
          modelId: storeyDialog.modelId,
          links,
        });
        await persistDialogTransform(storeyDialog.modelId, nudge);
        toast.success(t("ifc_viewer.link_storeys_saved"));
        exitStoreyCompare({ restoreTransform: false });
        setStoreyDialog(null);
        openNextStoreyDialog();
      } finally {
        setStoreyDialogSaving(false);
      }
    },
    [
      exitStoreyCompare,
      openNextStoreyDialog,
      persistDialogTransform,
      postToViewer,
      projectId,
      storeyDialog,
      t,
    ],
  );

  const handleStoreyDialogSkip = useCallback(
    async ({ persistProposals = true } = {}) => {
      if (storeyDialog?.modelId && persistProposals) {
        writeStoreyMap(projectId, storeyDialog.modelId, {
          links: (storeyDialog.proposals || []).map((row) => ({
            fromId: row.fromId,
            toId: row.toId,
          })),
          datumModelId: storeyDialog.datumModelId,
          confirmed: false,
        });
        const nudge = lastNudgeRef.current || {};
        if (nudge.x || nudge.y || nudge.z) {
          await persistDialogTransform(storeyDialog.modelId, nudge);
        }
      }
      exitStoreyCompare({ restoreTransform: false });
      setStoreyDialog(null);
      openNextStoreyDialog();
    },
    [
      exitStoreyCompare,
      openNextStoreyDialog,
      persistDialogTransform,
      projectId,
      storeyDialog,
    ],
  );

  const handleDialogNudge = useCallback(
    (offset) => {
      const baseline = compareBaselineRef.current;
      const modelId = storeyDialog?.modelId;
      if (!baseline || !modelId) return;
      const dx = clampNudgeMeters(offset.x || 0, NUDGE_XY_MAX_M);
      const dy = clampNudgeMeters(offset.y || 0, NUDGE_XY_MAX_M);
      const dz = clampNudgeMeters(offset.z || 0, NUDGE_Z_MAX_M);
      lastNudgeRef.current = { x: dx, y: dy, z: dz };
      postToViewer({
        type: "ifc:set-transform",
        modelId,
        transform: {
          ...baseline,
          x: (baseline.x || 0) + dx,
          y: (baseline.y || 0) + dy,
          z: (baseline.z || 0) + dz,
        },
      });
    },
    [postToViewer, storeyDialog],
  );

  const handleDialogSelectStorey = useCallback(
    ({ storeyId, elevation }) => {
      setStoreyFilter(storeyId || "");
      postToViewer({ type: "ifc:set-storey", storeyId: storeyId || null });
      if (Number.isFinite(elevation)) {
        const cut = metersFromMaybeMillimetres(elevation, NUDGE_Z_MAX_M * 4);
        postToViewer({
          type: "ifc:set-section",
          enabled: true,
          axis: "z",
          elevation: cut,
        });
        setSection((prev) => ({ ...prev, enabled: true, axis: "z" }));
      }
    },
    [postToViewer],
  );

  const handleOpenStoreyLink = useCallback(
    (modelId) => {
      if (!allowModels || !viewerReady) {
        toast.error(t("ifc_viewer.link_storeys_unavailable"));
        return;
      }
      const datum = pickDatumModel(models);
      if (!datum || String(datum.id) === String(modelId)) return;
      const cached = storeyProposalsRef.current[String(modelId)];
      if (cached) {
        setStoreyDialog(cached);
        return;
      }
      pendingOpenStoreyModelIdRef.current = modelId;
      postToViewer({ type: "ifc:request-storey-map", modelId });
    },
    [allowModels, models, postToViewer, t, viewerReady],
  );

  const canLinkDrawing = Boolean(canPersist && projectId);

  const handleOpenLinkDrawing = useCallback(() => {
    if (!canLinkDrawing) return;
    setLinkDrawingOpen(true);
  }, [canLinkDrawing]);

  const leaveLinkDrawingView = useCallback(
    ({ restoreView = true } = {}) => {
      for (const message of linkDrawingLeaveMessages({ restoreView })) {
        postToViewer(message);
      }
      if (!restoreView) return;
      ignoreDialogTransformsRef.current = false;
      const snap = linkDrawingViewRef.current;
      linkDrawingViewRef.current = null;
      if (snap?.section) setSection(snap.section);
      if (snap && Object.prototype.hasOwnProperty.call(snap, "storeyFilter")) {
        setStoreyFilter(snap.storeyFilter || "");
      }
    },
    [postToViewer],
  );

  const handleLinkDrawingStorey = useCallback(
    (storey) => {
      if (!linkDrawingViewRef.current) {
        linkDrawingViewRef.current = {
          section: { ...section },
          storeyFilter,
        };
      }
      // Overlay first so the viewer snapshots camera/section *before* birdview.
      // Do not set-storey — that hides unmapped RIV and looks like a rematch.
      ignoreDialogTransformsRef.current = true;
      const elevationM = Number.isFinite(storey?.elevation)
        ? metersFromMaybeMillimetres(storey.elevation, NUDGE_Z_MAX_M * 4)
        : null;
      for (const message of linkDrawingEnterMessages({ elevationM })) {
        postToViewer(message);
      }
      if (Number.isFinite(elevationM)) {
        setSection((prev) => ({ ...prev, enabled: true, axis: "z" }));
      }
    },
    [postToViewer, section, storeyFilter],
  );

  const handleSaveLinkDrawing = useCallback(
    async ({ drawingId, storey, control_points }) => {
      if (!projectId || !drawingId) return;
      try {
        setLinkDrawingSaving(true);
        await createDrawingIfcAlignment(projectId, {
          drawing_id: drawingId,
          ifc_model_id: pickDatumModelId(models),
          storey_name: storey?.name || null,
          z_offset: Number.isFinite(storey?.elevation) ? storey.elevation : 0,
          control_points,
        });
        toast.success(t("ifc_viewer.link_drawing_saved"));
        setLinkDrawingOpen(false);
        setOverlayTransform(null);
        leaveLinkDrawingView();
      } catch (_) {
        toast.error(t("ifc_viewer.link_drawing_error"));
      } finally {
        setLinkDrawingSaving(false);
      }
    },
    [projectId, models, postToViewer, t, leaveLinkDrawingView],
  );

  const handleUploadConfirm = useCallback(
    async ({ files, discipline, drawing_house_id: houseId, name }) => {
      const list = Array.from(files || []);
      if (!allowModels || list.length === 0 || !projectId) return;

      // Confirm while we still have the dialog click as a user gesture.
      // After upload/create awaits, window.confirm is often blocked and the
      // first create (without replace_existing) would just add a second RIV
      // when uniqueness is not enforced.
      const slotMatches = findExistingIfcModels(
        modelsRef.current,
        houseId,
        discipline,
      );
      const replaceConfirmed = slotMatches.length > 0;
      if (replaceConfirmed) {
        const existingName = slotMatches[0].name || "";
        const ok = window.confirm(
          t("ifc_viewer.confirm_replace_model", { name: existingName }),
        );
        if (!ok) return;
      }

      const aligningOntoExisting = modelsRef.current.length > 0;
      setUploading(true);
      let created = 0;
      let replacedCount = 0;
      const createdIds = [];
      const keptPlacementDisciplines = [];
      try {
        let index = 0;
        for (const file of list) {
          const uploaded = await uploadModuleDocuments(IFC_MODULE, projectId, [file]);
          const documentId = uploaded?.[0]?.id;
          if (!documentId) continue;
          const existingMatches = findExistingIfcModels(
            modelsRef.current,
            houseId,
            discipline,
          );
          const previous = pickPlacementSource(existingMatches);
          const kept = placementTransform(
            transformToReuse(previous) || DEFAULT_TRANSFORM,
            { keepUprightTilt: previous?.up_axis_confirmed === true },
          );
          const reuse =
            (replaceConfirmed || existingMatches.length > 0) &&
            hasSavedPlacement(kept);
          const modelPayload = {
            name:
              list.length === 1 && name
                ? name
                : file.name.replace(/\.[^/.]+$/, ""),
            document_id: documentId,
            discipline,
            drawing_house_id: houseId || null,
            sort_order: modelsRef.current.length + index,
            transform: reuse ? kept : DEFAULT_TRANSFORM,
          };

          const finishRow = async (row, prior, keptTransform) => {
            if (!row?.id) return;
            const copiedMap =
              prior?.id && copyStoreyMap(projectId, prior.id, row.id);
            if (prior?.id) copyPlacement(projectId, prior.id, row.id);
            if (!copiedMap) createdIds.push(row.id);
            if (!keptTransform || !hasSavedPlacement(keptTransform)) return;
            keptPlacementIdsRef.current.add(String(row.id));
            keptPlacementDisciplines.push(discipline);
            if (!canPersist) return;
            // Create may drop transform (new row after destroy). PUT copies
            // XYZ and sets up_axis_confirmed so rematch will not move it.
            try {
              await updateIfcModel(projectId, row.id, {
                transform: keptTransform,
                up_axis_confirmed: true,
              });
            } catch (_) {
              /* session still gets wanted via loadModels if create kept it */
            }
          };

          const { row, replaced, previous: replacedPrior } =
            await createOrReplaceIfcModel({
              projectId,
              payload: modelPayload,
              existingModels: existingMatches,
              replaceConfirmed: replaceConfirmed || existingMatches.length > 0,
              createIfcModel,
              deleteIfcModel,
            });
          const prior = replacedPrior || previous;
          await finishRow(row, replaced ? prior : null, reuse ? kept : null);
          if (row?.id) {
            const gone = new Set(existingMatches.map((model) => String(model.id)));
            modelsRef.current = [
              ...modelsRef.current.filter((model) => !gone.has(String(model.id))),
              row,
            ];
          }
          if (replaced) replacedCount += 1;
          index += 1;
          created += 1;
        }
        if (created === 0) {
          throw new Error("upload_failed");
        }
        setUploadDialogOpen(false);
        if (replacedCount > 0) {
          toast.success(t("ifc_viewer.model_replaced"));
        }
        [...new Set(keptPlacementDisciplines)].forEach((code) => {
          toast.success(
            t("ifc_viewer.placement_kept", { discipline: code || "IFC" }),
          );
        });
        // New discipline on an existing scene: rematch unplaced models.
        // Replace with copied XYZ must not look like a fresh align.
        pendingAlignToastRef.current =
          aligningOntoExisting && keptPlacementDisciplines.length === 0;
        const listAfter = await loadModels();
        const datumId = pickDatumModelId(listAfter);
        pendingStoreyLinkIdsRef.current = createdIds
          .map((id) => String(id))
          .filter((id) => datumId && id !== String(datumId));
        // Always rebuild the session so the iframe gets the new document URL.
        // Replace keeps the same model count, so the length-effect will not.
        if (listAfter.length > 0) {
          await openSession();
        }
      } catch (_) {
        toast.error(t("ifc_viewer.error_upload"));
        // Partial batch success may already have persisted models — refresh the list.
        if (created > 0) {
          await loadModels();
        }
      } finally {
        setUploading(false);
      }
    },
    [allowModels, projectId, loadModels, openSession, canPersist, t],
  );

  const handleDelete = useCallback(
    async (modelId) => {
      if (!allowModels || !projectId) return;
      try {
        await deleteIfcModel(projectId, modelId);
        const list = await loadModels();
        if (list.length > 0) await openSession();
        else {
          setSession(null);
          setSessionIssue(null);
          setViewerHealth("unknown");
        }
      } catch (_) {
        toast.error(t("ifc_viewer.error_delete"));
      }
    },
    [allowModels, projectId, loadModels, openSession, t],
  );

  handleHostActionRef.current = (data) => {
    const action = data?.action;
    if (action === "upload" && allowModels) {
      setUploadDialogOpen(true);
      return;
    }
    if (action === "reload" && allowModels) {
      void handleReloadModels();
      return;
    }
    if (action === "delete" && allowModels && data.modelId) {
      const name =
        modelsRef.current.find((m) => String(m.id) === String(data.modelId))
          ?.name || "";
      if (
        !window.confirm(t("ifc_viewer.confirm_delete_model", { name }))
      ) {
        return;
      }
      void handleDelete(data.modelId);
      return;
    }
    if (
      action === "set-default-visibility" &&
      allowModels &&
      data.modelId
    ) {
      void handleToggleHidden(data.modelId, data.visible !== false);
      return;
    }
    if (action === "link-storeys" && allowModels && data.modelId) {
      handleOpenStoreyLink(data.modelId);
      return;
    }
    if (action === "link-drawing" && canPersist) {
      handleOpenLinkDrawing();
      return;
    }
    if (action === "remerge" && allowModels) {
      handleRemerge();
      return;
    }
    if (action === "place" && allowModels) {
      handlePlaceModel();
    }
  };

  const pane = resolveIfcViewerPane({
    loading,
    creatingSession,
    sessionIssue,
    viewerEmbedUrl,
    modelCount: models.length,
    viewerHealth,
  });
  const paneIssue =
    sessionIssue || (viewerHealth === "down" ? "viewer_down" : null);

  return (
    <div
      ref={hostRef}
      className={`relative flex w-full min-h-0 overflow-hidden ${heightClass}`}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {canLinkDrawing && (
        <IfcLinkDrawingDialog
          t={t}
          open={linkDrawingOpen}
          projectId={projectId}
          initialDrawingId={linkDrawingId}
          storeys={storeys}
          overlayTransform={overlayTransform}
          saving={linkDrawingSaving}
          onStoreyPicked={handleLinkDrawingStorey}
          onOverlayStart={(payload) => {
            setOverlayTransform(null);
            postToViewer({ type: "drawing:overlay", enabled: true, ...payload });
          }}
          onOverlayUpdate={(patch) => {
            postToViewer({ type: "drawing:overlay", enabled: true, ...patch });
          }}
          onOverlayClear={(opts) => {
            setOverlayTransform(null);
            leaveLinkDrawingView(opts);
          }}
          onIfcPointWait={(fn) => {
            linkPairWaitRef.current = fn;
          }}
          onSave={handleSaveLinkDrawing}
          onClose={() => {
            linkPairWaitRef.current = null;
            setLinkDrawingOpen(false);
            setOverlayTransform(null);
            leaveLinkDrawingView();
          }}
        />
      )}

      {allowModels && (
        <IfcStoreyLinkDialog
          open={Boolean(storeyDialog)}
          proposal={storeyDialog}
          savedLinks={
            storeyDialog
              ? readStoreyMap(projectId, storeyDialog.modelId)?.links
              : null
          }
          saving={storeyDialogSaving}
          onClose={() => handleStoreyDialogSkip()}
          onSkip={() => handleStoreyDialogSkip()}
          onSave={handleStoreyDialogSave}
          onNudge={handleDialogNudge}
          onSelectStorey={handleDialogSelectStorey}
          datumBounds={
            storeyDialog
              ? modelBoundsById[String(storeyDialog.datumModelId)] || null
              : null
          }
          modelBounds={
            storeyDialog
              ? modelBoundsById[String(storeyDialog.modelId)] || null
              : null
          }
        />
      )}

      {allowModels && (
        <IfcUploadDialog
          open={uploadDialogOpen}
          houses={houses}
          disciplines={IFC_DISCIPLINES}
          uploading={uploading}
          onClose={() => {
            if (!uploading) setUploadDialogOpen(false);
          }}
          onConfirm={handleUploadConfirm}
        />
      )}

      <div className="relative min-h-0 h-full min-w-0 max-w-full flex-1 overflow-hidden bg-[#1e1f22] xl:rounded-[10px] xl:border xl:border-[#e5e8ec]">
        {pane === "iframe" ? (
          <>
            <iframe
              ref={iframeRef}
              key={session.session_id}
              title={t("ifc_viewer.iframe_title")}
              src={viewerEmbedUrl}
              className="h-full w-full border-0"
              allow="fullscreen"
              sandbox="allow-scripts allow-same-origin"
              onError={() => {
                readyWatchRef.current.iframeError = true;
                failEmbed("viewer_down");
              }}
            />
            {!viewerReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#1e1f22]/70">
                <Spinner variant="gradient" size={44} />
              </div>
            )}
          </>
        ) : pane === "loading" ? (
          <ResourceLoading label={t("ifc_viewer.loading")} />
        ) : pane === "issue" ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2.5 px-6 text-center text-sm text-amber-200">
            <ExclamationTriangleIcon className="h-9 w-9 opacity-70" aria-hidden="true" />
            {t(issueMessageKey(paneIssue))}
            <button
              type="button"
              onClick={handleRetryViewer}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-[7px] border border-white/20 bg-white px-3 py-2 text-[13px] font-semibold text-[#14171c]"
            >
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              {t("ifc_viewer.retry")}
            </button>
            {allowModels && paneIssue === "no_ready_models" && (
              <button
                type="button"
                onClick={() => setUploadDialogOpen(true)}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-[7px] border border-white/20 bg-white px-3 py-2 text-[13px] font-semibold text-[#14171c]"
              >
                <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                {t("ifc_viewer.upload")}
              </button>
            )}
          </div>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2.5 px-6 text-center text-sm text-gray-400">
            <CubeTransparentIcon className="h-9 w-9 opacity-40" aria-hidden="true" />
            {t("ifc_viewer.empty_viewer")}
            {allowModels && (
              <button
                type="button"
                onClick={() => setUploadDialogOpen(true)}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-[7px] border border-[#e5e8ec] bg-white px-3 py-2 text-[13px] font-semibold text-[#14171c]"
              >
                <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                {t("ifc_viewer.upload")}
              </button>
            )}
          </div>
        )}

        {focusPoint && (
          <div className="pointer-events-none absolute left-3 top-3 rounded-[8px] border border-white/10 bg-[#26272b]/90 px-2.5 py-1.5 text-[11px] text-gray-200 max-xl:left-auto max-xl:right-3 max-xl:top-[max(0.5rem,env(safe-area-inset-top,0px))]">
            {t("ifc_viewer.focus_from_drawing")}
            {focusPoint.storey_name ? ` — ${focusPoint.storey_name}` : ""}
          </div>
        )}
        {measureLabel && (
          <div className="pointer-events-none absolute right-3 top-3 rounded-[8px] border border-white/10 bg-[#26272b]/90 px-2.5 py-1.5 text-[11px] font-semibold text-[#8eb8ff]">
            {measureLabel}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
